import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { getFeaturedVideos } from '../services/api';
import { ChevronLeft, ChevronRight, Play, Users, ArrowRight, FileText } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { translations } from '../translations';
import TranscriptModal from './TranscriptModal';
import './FeaturedVideos.css';

export default function FeaturedVideos() {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const t = translations[language];
  
  const [videos, setVideos] = useState([]);
  const [allVideos, setAllVideos] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [showTranscript, setShowTranscript] = useState(false);

  const loadFeaturedVideos = useCallback(async () => {
    try {
      setLoading(true);
      const data = await getFeaturedVideos();
      setAllVideos(data.videos);
      
      // Filter videos by current UI language
      const filteredVideos = data.videos.filter(video => video.language === language);
      
      // If no videos for current language, fall back to all videos
      setVideos(filteredVideos.length > 0 ? filteredVideos : data.videos);
      setCurrentIndex(0); // Reset to first video
      setError(null);
    } catch (err) {
      console.error('Error loading featured videos:', err);
      setError('Failed to load featured videos');
    } finally {
      setLoading(false);
    }
  }, [language]);

  useEffect(() => {
    loadFeaturedVideos();
  }, [loadFeaturedVideos]);

  // Re-filter videos when UI language changes
  useEffect(() => {
    if (allVideos.length > 0) {
      const filteredVideos = allVideos.filter(video => video.language === language);
      
      // If no videos for current language, fall back to all videos
      setVideos(filteredVideos.length > 0 ? filteredVideos : allVideos);
      setCurrentIndex(0); // Reset to first video when language changes
      setIsPlaying(false); // Stop playing when switching
    }
  }, [language, allVideos]);

  const goToNext = () => {
    setCurrentIndex((prev) => (prev + 1) % videos.length);
    setIsPlaying(false);
  };

  const goToPrevious = () => {
    setCurrentIndex((prev) => (prev - 1 + videos.length) % videos.length);
    setIsPlaying(false);
  };

  const goToSlide = (index) => {
    setCurrentIndex(index);
    setIsPlaying(false);
  };

  const handleTranscript = () => {
    setShowTranscript(true);
  };

  const handleCloseTranscript = () => {
    setShowTranscript(false);
  };

  const getLanguageFlag = (lang) => {
    switch (lang) {
      case 'fi': return '🇫🇮';
      case 'en': return '🇬🇧';
      case 'sv': return '🇸🇪';
      default: return '🌐';
    }
  };

  const getLanguageDisplay = (lang) => {
    if (!t.featuredVideos?.language) {
      // Fallback if translations not loaded
      switch (lang) {
        case 'fi': return 'Finnish';
        case 'en': return 'English';
        case 'sv': return 'Swedish';
        default: return lang.toUpperCase();
      }
    }
    return t.featuredVideos.language[lang] || lang.toUpperCase();
  };

  const getQualityDisplay = (quality) => {
    switch (quality) {
      case 'qh': return '1080p';
      case 'qm': return '720p';
      case 'ql': return '480p';
      default: return quality;
    }
  };

  if (loading) {
    return (
      <section className="featured-videos">
        <div className="featured-container">
          <h2>{t.featuredVideos?.title || 'Featured Educational Animations'}</h2>
          <div className="featured-loading">
            <div className="loading-spinner"></div>
            <p>{t.featuredVideos?.loading || 'Loading featured videos...'}</p>
          </div>
        </div>
      </section>
    );
  }

  if (error || videos.length === 0) {
    return null;
  }

  const currentVideo = videos[currentIndex];

  return (
    <section className="featured-videos">
      <div className="featured-container">
        <div className="featured-header">
          <div className="featured-header-content">
            <h2>{t.featuredVideos?.title || 'Featured Educational Animations'}</h2>
            <p className="featured-subtitle">
              {t.featuredVideos?.subtitle || 'Explore amazing educational content created with AnimLearn'}
            </p>
          </div>
        </div>

        <div className="featured-carousel">
          {/* Navigation Buttons */}
          {videos.length > 1 && (
            <>
              <button 
                className="featured-nav-btn prev" 
                onClick={goToPrevious}
                aria-label="Previous video"
              >
                <ChevronLeft size={28} />
              </button>
              <button 
                className="featured-nav-btn next" 
                onClick={goToNext}
                aria-label="Next video"
              >
                <ChevronRight size={28} />
              </button>
            </>
          )}

          {/* Main Video Display */}
          <div className="featured-video-wrapper">
            <div className="featured-video-container">
              {!isPlaying && currentVideo.thumbnail_url ? (
                <div 
                  className="featured-thumbnail"
                  onClick={() => setIsPlaying(true)}
                >
                  <img 
                    src={currentVideo.thumbnail_url} 
                    alt={currentVideo.scene_name}
                  />
                  <div className="featured-play-overlay">
                    <div className="featured-play-button">
                      <Play size={56} fill="white" />
                    </div>
                  </div>
                </div>
              ) : (
                <div className="featured-video-player-wrapper">
                  <video
                    key={currentVideo.id}
                    controls
                    controlsList="nodownload"
                    autoPlay={isPlaying}
                    className="featured-video-player"
                    onEnded={() => setIsPlaying(false)}
                  >
                    <source src={currentVideo.video_url} type="video/mp4" />
                    Your browser does not support the video tag.
                  </video>
                </div>
              )}
            </div>

            {/* Video Info */}
            <div className="featured-video-info">
              <div className="featured-video-header">
                <h3>{currentVideo.scene_name}</h3>
                {currentVideo.transcript_markdown && (
                  <button 
                    className="featured-transcript-btn"
                    onClick={handleTranscript}
                    title={t.featuredVideos?.transcript || 'View transcript'}
                  >
                    <FileText size={20} />
                    <span>{t.featuredVideos?.transcript || 'Transcript'}</span>
                  </button>
                )}
              </div>
              <div className="featured-meta">
                <span className="featured-badge language">
                  <span className="badge-flag">{getLanguageFlag(currentVideo.language)}</span>
                  <span className="badge-text">{getLanguageDisplay(currentVideo.language)}</span>
                </span>
                <span className="featured-badge quality">
                  {getQualityDisplay(currentVideo.quality)}
                </span>
                {currentVideo.duration && (
                  <span className="featured-badge duration">
                    {Math.floor(currentVideo.duration / 60)}:{String(currentVideo.duration % 60).padStart(2, '0')}
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* Thumbnail Navigation */}
          {videos.length > 1 && (
            <div className="featured-thumbnails">
              {videos.map((video, index) => (
                <button
                  key={video.id}
                  className={`featured-thumbnail-btn ${index === currentIndex ? 'active' : ''}`}
                  onClick={() => goToSlide(index)}
                  aria-label={`View ${video.scene_name}`}
                >
                  {video.thumbnail_url ? (
                    <img src={video.thumbnail_url} alt={video.scene_name} />
                  ) : (
                    <div className="thumbnail-placeholder">
                      🎬
                    </div>
                  )}
                  <div className="thumbnail-overlay">
                    <span className="thumbnail-title">{video.scene_name}</span>
                  </div>
                </button>
              ))}
            </div>
          )}

          {/* Dot Indicators */}
          {videos.length > 1 && (
            <div className="featured-indicators">
              {videos.map((_, index) => (
                <button
                  key={index}
                  className={`featured-indicator ${index === currentIndex ? 'active' : ''}`}
                  onClick={() => goToSlide(index)}
                  aria-label={`Go to video ${index + 1}`}
                />
              ))}
            </div>
          )}

          {/* Counter */}
          {videos.length > 1 && (
            <div className="featured-counter">
              <span className="current">{currentIndex + 1}</span>
              <span className="separator">/</span>
              <span className="total">{videos.length}</span>
            </div>
          )}
        </div>

        {/* Community Gallery CTA Banner */}
        <div className="community-gallery-cta">
          <div className="cta-content">
            <div className="cta-icon">
              <Users size={48} />
            </div>
            <div className="cta-text">
              <h3>{t.featuredVideos?.communityGallery?.title || 'Discover More Amazing Creations'}</h3>
              <p>{t.featuredVideos?.communityGallery?.description || 'Browse hundreds of educational animations created by our community'}</p>
            </div>
          </div>
          <button 
            className="cta-button"
            onClick={() => navigate('/gallery')}
          >
            <span>{t.featuredVideos?.communityGallery?.button || 'Explore Community Gallery'}</span>
            <ArrowRight size={20} />
          </button>
        </div>
      </div>

      {/* Transcript Modal */}
      {showTranscript && currentVideo.transcript_markdown && (
        <TranscriptModal
          open={showTranscript}
          onClose={handleCloseTranscript}
          transcript={currentVideo.transcript_markdown}
        />
      )}
    </section>
  );
}